/**
 * Purchase history view-model (Task 21).
 * Auto-classified rows may be category-corrected; any purchase may be flagged recurring.
 */

import type { Cents } from '../../domain/money';
import type { CategorySource, SpendCategory, TxnChannel } from '../../domain/recommend/types';
import { isAutoClassifiedCategory } from '../../domain/recommend/categoryObservations';

export type PurchaseHistoryRow = {
  transaction_id: string;
  merchant_name: string;
  merchant_id: string | null;
  amount_cents: Cents;
  transaction_date: string;
  category: SpendCategory | null;
  category_src: CategorySource;
  channel: TxnChannel;
  card_label: string;
  /** Digits-only display suffix for CSV export (Task 22). */
  card_suffix: string;
  expected_reward_cents: Cents | null;
  /** Optional Item(s) from Quick Purchase (`transactions.description`). */
  description: string | null;
  notes: string | null;
  /** True when category was engine-chosen and can be corrected. */
  can_correct_category: boolean;
  /** True when not yet linked to a recurring_charges row. */
  can_flag_recurring: boolean;
  recurring_charge_id: string | null;
};

export type BuildPurchaseHistoryRowInput = {
  transaction_id: string;
  merchant_name: string;
  merchant_id: string | null;
  amount_cents: Cents;
  transaction_date: string;
  category: SpendCategory | null;
  category_src: CategorySource;
  channel: TxnChannel;
  card_label: string;
  card_suffix: string;
  expected_reward_cents: Cents | null;
  description: string | null;
  notes: string | null;
  recurring_charge_id: string | null;
};

export function buildPurchaseHistoryRow(input: BuildPurchaseHistoryRowInput): PurchaseHistoryRow {
  const hasMerchant = input.merchant_id !== null;
  return {
    transaction_id: input.transaction_id,
    merchant_name: input.merchant_name,
    merchant_id: input.merchant_id,
    amount_cents: input.amount_cents,
    transaction_date: input.transaction_date,
    category: input.category,
    category_src: input.category_src,
    channel: input.channel,
    card_label: input.card_label,
    card_suffix: input.card_suffix,
    expected_reward_cents: input.expected_reward_cents,
    description: input.description,
    notes: input.notes,
    can_correct_category: hasMerchant && isAutoClassifiedCategory(input.category_src),
    can_flag_recurring: input.recurring_charge_id === null,
    recurring_charge_id: input.recurring_charge_id,
  };
}
